import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import CertificatesCarousel from "@/components/CertificatesCarousel";
import SEO from "@/components/SEO";

const education = [
  { year: "2003", label: "Diploma in General Medicine — Rostov Basic Medical College, Russia · Qualification: Feldsher / Medical Practitioner" },
  { year: "2019", label: "Acne Therapy: From Cosmetology to Dermatology" },
  { year: "2019", label: "Rosacea from the Perspective of a Dermatocosmetologist" },
  { year: "2019", label: "Biorevitalization: Comparative Analysis of Products" },
  { year: "2019", label: "Plasmotherapy / Plasmolifting" },
  { year: "2020", label: "Advanced Botulinum Toxin Techniques for the Face and Neck" },
  { year: "2025", label: "Cadaver Dissection Course in Applied Anatomy and Injection Cosmetology — Istanbul" }
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 md:pt-40 bg-[#FAF8F5]">
      <SEO
        title="About Natalia | Master Aesthetician in Kirkland, WA | Natalia Medical Aesthetics"
        description="Meet Natalia — Master Aesthetician with medical education and advanced professional training. Morpheus8, HydraFacial and IPL Lumecca in Kirkland, WA."
        path="/about"
      />
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-end">
          <div className="lg:col-span-6 fade-up">
            <div className="overline mb-6">About Natalia</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight">
              A medical career <em className="italic text-[#8A847C]">devoted to nuance.</em>
            </h1>
            <p className="mt-10 text-[1.05rem] text-[#5C5C5C] leading-relaxed max-w-lg">
              Twenty years of medicine, refined into a single-minded pursuit: to help
              clients look like themselves, only well-rested.
            </p>
          </div>
          <div className="lg:col-span-6 fade-in">
            <div className="image-hover rounded-[28px] overflow-hidden aspect-[4/5] bg-[#F3EFEA] max-w-[560px] ml-auto">
              <img src="https://customer-assets.emergentagent.com/job_aesthetic-premium-1/artifacts/kxycemvo_IMG_3122.JPG" alt="Natalia" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-[#E5E0DA] mt-24 md:mt-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <div className="overline">Philosophy</div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Restraint, precision, <em className="italic text-[#8A847C]">and the art of not doing too much.</em>
            </h2>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10 max-w-3xl">
              <p className="text-[#5C5C5C] leading-relaxed">
                My practice is grounded in medicine and shaped by decades of listening.
                I believe the best aesthetic results are the ones no one can point to —
                they simply make you look well.
              </p>
              <p className="text-[#5C5C5C] leading-relaxed">
                I only offer treatments I would perform on my closest friends and family.
                Every protocol is FDA-cleared or Health Canada-approved, and every plan
                is personal — never a menu.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience & Education */}
      <section className="bg-[#F3EFEA]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <div className="lg:col-span-4">
              <div className="overline mb-6">Experience</div>
              <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">Education & training</h3>
            </div>
            <div className="lg:col-span-8">
              <ul className="divide-y divide-[#E5E0DA]">
                {education.map((e) => (
                  <li key={e.year} className="py-6 md:py-8 grid grid-cols-12 gap-6 items-baseline">
                    <span className="col-span-3 md:col-span-2 font-serif text-3xl md:text-4xl tracking-tight text-[#8A847C]">{e.year}</span>
                    <span className="col-span-9 md:col-span-10 text-[#1A1A1A] text-base md:text-lg leading-snug">{e.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <div className="overline mb-6">Advanced training</div>
            <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">Certifications</h3>
          </div>
          <div className="lg:col-span-8">
            <CertificatesCarousel />
          </div>
        </div>
      </section>

      {/* Clinic atmosphere — text only, no stock imagery */}
      <section className="border-t border-[#E5E0DA]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <div className="lg:col-span-4">
              <div className="overline mb-6">The clinic</div>
              <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
                A quiet, considered <em className="italic text-[#8A847C]">private space.</em>
              </h3>
            </div>
            <div className="lg:col-span-8 max-w-2xl">
              <p className="text-[#5C5C5C] leading-relaxed text-lg">
                Warm light, natural materials, and single-client scheduling. Our clinic
                is designed to feel unhurried — a place to breathe, to be listened to,
                and to be well cared for.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 pb-24 md:pb-32">
        <div className="bg-[#1A1A1A] text-white rounded-[28px] p-12 md:p-24 text-center">
          <div className="overline !text-[#8A847C] mb-8">Begin</div>
          <h3 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl mx-auto">
            Design a plan that feels <em className="italic">entirely yours.</em>
          </h3>
          <div className="mt-12">
            <Link to="/book" data-testid="about-cta-btn" className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] rounded-full px-10 py-5 text-[0.85rem] font-medium font-medium hover:bg-[#F3EFEA] transition-colors">
              Book Consultation <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
